import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ArrowDropUp as ArrowDropUpIcon, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useGetProfileQuery } from '../features/api/rest/apiProfileSlice';
import { useGetQuestionVoteQuery, useUpdateQuestionVoteMutation, useGetAnswerVoteQuery, useUpdateAnswerVoteMutation } from '../features/api/rest/apiVoting';

function VotingArrow({ questionId, answerId = null, totScore, typology }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { ean } = useParams();
  const [score, setScore] = useState(totScore);

  const [updateQuestionVote] = useUpdateQuestionVoteMutation();
  const [updateAnswerVote] = useUpdateAnswerVoteMutation();

  const { status } = useGetProfileQuery(undefined, { refetchOnMountOrArgChange: true });
  const { questionVote } = useGetQuestionVoteQuery(
    { ean, id: questionId },
    {
      selectFromResult: ({ data }) => ({
        questionVote: data?.vote,
      }),
    }
  );
  const { answerVote } = useGetAnswerVoteQuery(
    { ean, Q: questionId, id: answerId },
    {
      selectFromResult: ({ data }) => ({
        answerVote: data?.vote,
      }),
    }
  );

  const vote = typology === 'Question' ? questionVote : answerVote;

  const handleVoteClick = async (type) => {
    if (status === 'fulfilled') handleNewVote(type);
    else navigate('/login', { state: { next: `${location.pathname}${location.search}` } });
  };

  const handleNewVote = async (type) => {
    if (type === vote) {
      try {
        if (typology === 'Question') await updateQuestionVote({ payload: { vote: 'CLEAR' }, ean, id: questionId }).unwrap();
        else await updateAnswerVote({ payload: { vote: 'CLEAR' }, ean, Q: questionId, id: answerId }).unwrap();
        setScore(type === 'UP' ? score - 1 : score + 1);
      } catch (err) {
        console.error(err);
      }
    } else {
      try {
        if (typology === 'Question') await updateQuestionVote({ payload: { vote: type }, ean, id: questionId }).unwrap();
        else await updateAnswerVote({ payload: { vote: type }, ean, Q: questionId, id: answerId }).unwrap();
        if (vote === 'CLEAR') setScore(type === 'UP' ? score + 1 : score - 1);
        else setScore(type === 'UP' ? score + 2 : score - 2);
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <Grid>
      <Tooltip title={t(`questionsAnswers.votes${typology}.up`)} placement="right">
        <IconButton onClick={() => handleVoteClick('UP')} sx={{ py: 0 }}>
          <ArrowDropUpIcon sx={{ fontSize: 40, color: vote === 'UP' ? '#2196f3' : 'gray' }} />
        </IconButton>
      </Tooltip>
      <Typography sx={{ textAlign: 'center' }}>{score}</Typography>
      <Tooltip title={t(`questionsAnswers.votes${typology}.down`)} placement="right">
        <IconButton onClick={() => handleVoteClick('DOWN')} sx={{ py: 0 }}>
          <ArrowDropDownIcon sx={{ fontSize: 40, color: vote === 'DOWN' ? '#2196f3' : 'gray' }} />
        </IconButton>
      </Tooltip>
    </Grid>
  );
}

VotingArrow.propTypes = {
  questionId: PropTypes.number.isRequired,
  answerId: PropTypes.number,
  totScore: PropTypes.number.isRequired,
  typology: PropTypes.string.isRequired,
};

export default VotingArrow;
